import { MuiAlert, strIsNullOrEmpty, Typography } from '@fox/atoms';
import { IWorkBenchLayout } from '@fox/blocks';
import { ProviderSelection } from '@fox/finance';
import {
	EventCapacityData,
	FormDefinitionResponse,
	FormErrorContextProvider,
	FormErrorContextType,
	IQuestionAnswer,
	ISection,
	QuestionStyles,
	ReservationUpdate,
	Survey,
	SurveyState
} from '@fox/forms-runtime';
import { stringToEntityType } from '@fox/model';
import moment from 'moment';
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTheme } from 'styled-components';
import { useContentContext } from '../content-context';
import { useFinancialContext } from '../financial-config-context';
import { getRcTokenAsync, tokenFailable } from '../GoogleReCaptcha/Recaptcha';
import { convertMemberPortalToSfMethodEnumValue } from '../Membership/financial-details/shared';
import { useMemberEntityData } from '../Membership/query-hooks/query-implementations';
import { complete, fetchPointsTotal, uploadFile } from './api-helpers/form-api-helpers';
import {
	createReservation,
	createTicketReservation,
	expireTicketReservation,
	extendReservation,
	fetchTicketDetails,
	findReservationById,
	updateReservationCount,
	validateRsvpResponse
} from './api-helpers/form-event-api-helpers';
import {
	completeCardIntent,
	completeDebitIntent,
	createDirectDebit,
	fetchAccountInfo,
	fetchPlans,
	fetchTaxRates,
	prorateFee,
	setupIntent,
	updateIntent,
	validateDiscount
} from './api-helpers/form-financial-api-helpers';
import { ValidateAndCreateFormResponseInputModel, ValidateAndSubmitFormResponseResult } from './contracts';

async function submitForm(
	model: ValidateAndCreateFormResponseInputModel,
	requestToken: string
): Promise<ValidateAndSubmitFormResponseResult> {
	try {
		const res = await fetch('/Umbraco/Api/Membership/ValidateAndSubmitForm', {
			method: 'POST',
			body: JSON.stringify(model),
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				RequestVerificationToken: requestToken
			}
		});
		if (res.ok) {
			const response = await res.json();
			return response as ValidateAndSubmitFormResponseResult;
		} else {
			const err = await res.text();
			return { submitted: false, message: err };
		}
	} catch (e) {
		return { submitted: false, message: `Error: ${e.message}` };
	}
}

function useFormDefinitionData(input: { contentId: string; formId: number; requestToken: string }): {
	formDefinition?: FormDefinitionResponse;
	eventCapacityData?: EventCapacityData;
	error: string;
	loading: boolean;
} {
	const [error, setError] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [formDefinition, setFormDefinition] = useState<FormDefinitionResponse | undefined>(undefined);
	const [eventCapacityData, setEventCapacityData] = useState<EventCapacityData | undefined>(undefined);

	const { contentId, formId, requestToken } = input;

	const fetchData = async () => {
		try {
			setLoading(true);
			setError('');
			const formRes = await fetch(`/Umbraco/Api/Membership/SwiftFoxFormDefinition?contentId=${contentId}&formId=${formId}`, {
				method: 'GET',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
					RequestVerificationToken: requestToken
				}
			});
			if (!formRes.ok) {
				const err = await formRes.text();
				setLoading(false);
				setError(err);
				return;
			}

			const response = await formRes.json();
			if (response.success !== true) {
				const err = response.message;
				setLoading(false);
				setError(err);
				return;
			}

			const formDef = response.result as FormDefinitionResponse;
			const eventId = formDef.ownerType === 'Event' ? formDef.ownerId : undefined;
			if (!eventId) {
				//no need for event data, just set form deinition and we're done
				setFormDefinition(response.result);
				setLoading(false);
				return;
			}

			const eventRes = await fetch(
				`/Umbraco/Api/Membership/EventCapacityStatus?contentId=${contentId}&eventId=${encodeURIComponent(eventId)}`,
				{
					method: 'GET',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
						RequestVerificationToken: requestToken
					}
				}
			);
			if (!eventRes.ok) {
				const err = await eventRes.text();
				setLoading(false);
				setError(err);
				return;
			}

			const eventResponse = await eventRes.json();
			if (eventResponse.success !== true) {
				setError(eventResponse.message);
				setLoading(false);
				return;
			}

			setFormDefinition(formDef);
			setEventCapacityData(eventResponse.result as EventCapacityData);
			setLoading(false);
			return;
		} catch (e) {
			setLoading(false);
			setError('Error: Something went wrong fetching form definition.');
		}
	};

	useEffect(() => {
		fetchData();
		return () => {
			setError('');
			setLoading(false);
			setFormDefinition(undefined);
		};
	}, []);

	return { formDefinition, eventCapacityData, error, loading };
}

export function SwiftFoxForm({ formId, backgroundColour, minHeight }: { formId: number; minHeight?: number; backgroundColour?: string }) {
	const { contentId, requestToken } = useContentContext();
	const { typography } = useTheme();
	const { eventCapacityData, formDefinition, error, loading } = useFormDefinitionData({ contentId, formId, requestToken });
	const { data: targetData, error: targetError, loading: targetLoading } = useMemberEntityData({ contentId, requestToken });
	const { currencies, providers, taxLabel, defaultTaxRate } = useFinancialContext();
	const [formRequestId] = useState<string>(nanoid());
	const [errors, setErrors] = useState<FormErrorContextType['errors']>({});
	const [surveyResponse, setSurveyResponse] = useState<string>('');
	const [surveyState, setSurveyState] = useState<SurveyState>(SurveyState.undefined);
	const { executeRecaptcha } = useGoogleReCaptcha();

	const styleOverrides: Partial<QuestionStyles> = {
		generalFormStyle: {},
		questionAnswerStyle: {},
		questionDescriptionStyle: {},
		questionLabelStyle: {}
	};

	if (backgroundColour) {
		styleOverrides.generalFormStyle.backgroundColour = backgroundColour;
	}
	if (minHeight > 0) {
		styleOverrides.generalFormStyle.minHeight = `${minHeight}px`;
	}
	if (typography?.fontFamily) {
		styleOverrides.questionAnswerStyle.fontFamily = typography.fontFamily;
		styleOverrides.questionDescriptionStyle.fontFamily = typography.fontFamily;
		styleOverrides.questionLabelStyle.fontFamily = typography.fontFamily;
	}

	if (error || targetError) return <MuiAlert severity="error">{error || targetError}</MuiAlert>;
	if (loading || targetLoading || !formDefinition) return <></>;
	if (!targetData) {
		return <MuiAlert severity="error">Missing SwiftFoxConfig.</MuiAlert>;
	}

	const expiresAt = moment(formDefinition?.expiresAt);
	if (expiresAt.isValid() && expiresAt.isBefore(moment())) {
		const message =
			formDefinition?.expirationMessage ||
			(formDefinition?.ownerType === 'Event'
				? 'Sorry, registration for this event is now closed.'
				: 'Sorry, this form is no longer accepting responses.');

		return <Typography>{message}</Typography>;
	}

	return (
		<FormErrorContextProvider
			errors={errors}
			setErrors={setErrors}
			clearSubmissionState={() => {
				setSurveyState(SurveyState.undefined);
				setSurveyResponse('');
			}}
		>
			<Survey
				eventCapacityData={eventCapacityData}
				styleOverrides={styleOverrides}
				targetInfo={targetData.personId ? { ownerId: targetData?.personId, ownerType: 'Person' } : null}
				isPreviewMode={false}
				formDef={formDefinition}
				formRequestId={formRequestId}
				surveyState={surveyState}
				onSubmit={async answers => {
					setErrors({});
					setSurveyState(SurveyState.submitting);
					const recaptchaTokenResponse = getRcTokenAsync('SwiftFoxForm', executeRecaptcha);
					const rcToken = await tokenFailable(recaptchaTokenResponse);
					if (!rcToken) {
						setSurveyResponse('Failed captcha.');
						setSurveyState(SurveyState.error);
						return;
					}
					const res = await submitForm(
						{
							answers,
							contentId,
							formId,
							isExternalRequest: false,
							tenant: targetData.tenant,
							requestId: formRequestId,
							target: targetData.personId ? { entityId: targetData?.personId, entityType: stringToEntityType('Person') } : null,
							rcToken
						},
						requestToken
					);
					if (res.submitted) {
						setErrors({});
						setSurveyState(SurveyState.submitted);
					} else {
						setSurveyResponse(res?.message || 'Failed to submit survey response');
						setSurveyState(SurveyState.error);
						const errors = res.errors || {};
						setErrors(errors);
					}
					return res;
				}}
				onSubmitted={async res => console.log('Survey submitted')}
				surveyResponse={surveyResponse}
				submitButtonDisabled={surveyState === SurveyState.submitting || surveyState === SurveyState.submitted}
				inLocalSurveyMode={true}
				financialApiHelpers={{
					fetchCurrencies: async () => {
						return currencies;
					},
					getProviders: async () => {
						if (!providers?.length) {
							return [];
						}
						return providers.map(p => ({ ...p, method: convertMemberPortalToSfMethodEnumValue(p.method) }));
					},
					fetchTaxRates: async () => {
						const rates = await fetchTaxRates(contentId, requestToken);

						return {
							rates: rates?.filter(t => t.active)?.map(t => ({ id: t.id, rate: t.rate, default: t.id === defaultTaxRate })),
							label: taxLabel
						};
					},
					fetchPlans: async (input: { ids?: string | null }) => {
						return await fetchPlans(contentId, requestToken, input.ids);
					},

					getAccountInfo: async (input: { ownerId?: string; ownerType?: string }) => {
						const res = await fetchAccountInfo(contentId, requestToken, input.ownerId, input.ownerType);
						if (res.error) {
							return { message: res.error, success: false };
						}
						return {
							account: { fields: [], lineItems: res.data },
							success: true
						};
					},
					completeCardIntent: async (input: { intentId: string; data: string }) => {
						const res = await completeCardIntent(contentId, requestToken, input.intentId, input.data);
						if (res.error) {
							return { success: false };
						}
						return { success: true, methodId: res.id, method: 'CREDIT_CARD' };
					},
					completeDebit: async (input: {
						intentId?: string;
						data?: string;
						accountName?: string;
						accountNumber?: string;
						bsb?: string;
						provider?: ProviderSelection;
					}) => {
						const { accountName, accountNumber, bsb, data: dataInput, intentId, provider } = input;
						if (intentId && dataInput) {
							const res = await completeDebitIntent(contentId, requestToken, intentId, dataInput);

							if (res.error) {
								return { success: false };
							}
							return { success: true, methodId: res.id, method: 'DIRECT_DEBIT' };
						} else if (
							!strIsNullOrEmpty(bsb) &&
							!strIsNullOrEmpty(accountNumber) &&
							!strIsNullOrEmpty(accountName) &&
							!strIsNullOrEmpty(provider?.provider)
						) {
							const result = await createDirectDebit({
								contentId,
								requestToken,
								ownerType: 'FormRequest',
								ownerId: formRequestId,
								bsb,
								account: accountNumber,
								accountName,
								sourceId: formRequestId,
								provider: provider!.provider
							});

							if (result.error) {
								return { success: false };
							}
							if (result.id) {
								return {
									success: true,
									method: 'DIRECT_DEBIT',
									methodId: result.id
								};
							} else {
								console.log('Missing return values when creating direct debit.');
								return {
									success: false
								};
							}
						}
						return { success: false };
					},
					prorateFee: async (input: { feeId: number }) => {
						return await prorateFee(contentId, requestToken, input.feeId);
					},
					setupIntent: async (input: { provider: ProviderSelection; name: string; email: string }) => {
						const { provider, name, email } = input;
						return await setupIntent({
							contentId,
							requestToken,
							email,
							name,
							provider,
							ownerId: formRequestId,
							ownerType: 'FormRequest'
						});
					},
					updateIntent: async (input: { provider: ProviderSelection; amount: number; intentId: string }) => {
						return await updateIntent(
							contentId,
							requestToken,
							input.amount,
							input.intentId,
							input.provider.method,
							input.provider.provider
						);
					},
					validateDiscount: async (input: { discountToApply: string; subscriptionSelections: string[]; ownerId?: string }) => {
						const { discountToApply, subscriptionSelections, ownerId } = input;
						const res = await validateDiscount(contentId, requestToken, discountToApply, subscriptionSelections, ownerId);
						if (res.error) {
							return { success: false, failReason: res.error };
						}

						return {
							success: true,
							discount: res.data
						};
					}
				}}
				eventApiHelpers={{
					createReservation: async (input: { eventId: string; count?: number }) => {
						return await createReservation({ contentId, requestToken, ...input });
					},
					createTicketReservation: async (input: {
						eventId: string;
						ticketId?: string;
						count?: number;
						reservationId?: string;
						enforceCount?: boolean;
					}) => {
						return await createTicketReservation({ contentId, requestToken, ...input });
					},
					expireTicketReservation: async (input: { eventId: string; reservationId: string }) => {
						return await expireTicketReservation({ contentId, requestToken, ...input });
					},
					extendReservation: async (input: { reservationId: string; messageKey: string; safeExpiry?: boolean }) => {
						return await extendReservation({ contentId, requestToken, ...input });
					},
					findReservationById: async (input: { contentId: string; requestToken: string; reservationId?: string }) => {
						return await findReservationById({ contentId, requestToken, ...input });
					},
					getTicketDetails: async (input: {
						contentId: string;
						requestToken: string;
						eventId: string;
						allowedTickets: string[];
					}) => {
						return await fetchTicketDetails({ contentId, requestToken, ...input });
					},
					updateReservationCount: async (input: { reservationId: string; operation: ReservationUpdate; count: number }) => {
						return await updateReservationCount({ contentId, requestToken, ...input });
					},
					validateRsvpResponse: async (input: { reservationId: string }) => {
						return await validateRsvpResponse({ contentId, requestToken, reservationId: input.reservationId });
					}
				}}
				getPointsTotal={async (input: { sections: ISection[]; layout: IWorkBenchLayout | undefined; answers: IQuestionAnswer[], formId: number }) =>
					await fetchPointsTotal({ contentId, requestToken, ...input })
				}
				complete={async (input: { searchString: string; types?: string[]; categories?: string[]; signedParams?: string }) =>
					await complete({ contentId, requestToken, ...input })
				}
				uploadFile={async (input: { file: File; formId: number }) => await uploadFile({ requestToken, contentId, ...input })}
			/>
		</FormErrorContextProvider>
	);
}
